import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@radix-ui/themes/styles.css";
import { Theme } from "@radix-ui/themes";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { UserProfileProvider } from "./contexts/UserProfileContext";
import { useTheme } from "./hooks/useTheme";
import { useAuth } from "./hooks/useAuth";
import { useUserRole } from "./hooks/useUserRole";
import { Login } from "./components/auth/Login";
import { UpdatePassword } from "./components/auth/UpdatePassword";
import { ResetPassword } from "./components/auth/ResetPassword";
import { Activities } from "./components/pages/activities";
import { About } from "./components/pages/About";
import { PrivacyPolicy } from "./components/pages/PrivacyPolicy";
import { TermsOfUse } from "./components/pages/TermsOfUse";
import { ProofingRouteSwitch } from "./components/pages/proofing/ProofingRouteSwitch";
import { ProofCapture } from "./components/pages/proofing/ProofCapture";
import { Approbation, ApprobationById } from "./components/pages/approbation";
import { ManageLayout } from "./components/pages/manage";
import { ManageUsers } from "./components/pages/manage/users";
import { ManageTerritories } from "./components/pages/manage/territories";
import { ManageStores } from "./components/pages/manage/stores";
import { ManageBanners } from "./components/pages/manage/banners";
import { ManageReps } from "./components/pages/manage/reps";
import { ManageProducts } from "./components/pages/manage/products";
import { ManageBrands } from "./components/pages/manage/brands";
import { ManageSuppliers } from "./components/pages/manage/suppliers";
import { ManagePeriods } from "./components/pages/manage/periods";
import { ManageStoreSets } from "./components/pages/manage/store-sets";
import { ManageStoreActivities } from "./components/pages/manage/store-activities";
import { ManageAuditors } from "./components/pages/manage/auditors";
import { ManageAgencies } from "./components/pages/manage/agencies";
import { Import } from "./components/pages/manage/import";
import { Audit } from "./components/pages/audit";
import { OnePager } from "./components/pages/one-pager";
import { Toolbar } from "./components/layout/Toolbar";
import { ReportPreview } from "./components/pages/dev/ReportPreview";
import { CookieConsentBanner } from "./components/common/CookieConsent";
import "./App.css";

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      <Toolbar />
      <div style={{ flex: 1, width: "100%", overflow: "auto" }}>{children}</div>
    </div>
  );
}

// Route protection for admin-only sections (Manage, Approbation)
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { loading: roleLoading, isAdmin } = useUserRole();

  if (roleLoading) {
    return <div>Loading...</div>;
  }

  if (!isAdmin) {
    return <Navigate to="/proofing" replace />;
  }

  return <>{children}</>;
}

// Route protection for rep OR admin sections (Proofing view-as)
function RepOrAdminRoute({ children }: { children: React.ReactNode }) {
  const { loading: roleLoading, isRep, isAdmin } = useUserRole();

  if (roleLoading) {
    return <div>Loading...</div>;
  }

  if (!isRep && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

// Route protection for auditor-only sections (Audit)
function AuditorRoute({ children }: { children: React.ReactNode }) {
  const { loading: roleLoading, isAuditor } = useUserRole();

  if (roleLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuditor) {
    // Return 404 for non-auditors trying to access audit routes
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <h1>404 - Not Found</h1>
        <p>The page you're looking for doesn't exist.</p>
      </div>
    );
  }

  return <>{children}</>;
}

// Wrapper that forces light theme for public pages (not designed for dark mode)
function PublicRoute({ children }: { children: React.ReactNode }) {
  return <Theme appearance="light">{children}</Theme>;
}

// Root route: always show the one-pager landing page
function RootRoute() {
  return <OnePager />;
}

function App({ appearance }: { appearance: "light" | "dark" }) {
  return (
    <Theme
      accentColor="iris"
      grayColor="sage"
      radius="medium"
      scaling="100%"
      appearance={appearance}
    >
      <BrowserRouter>
        <Routes>
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path="/reset-password"
            element={
              <PublicRoute>
                <ResetPassword />
              </PublicRoute>
            }
          />
          <Route
            path="/update-password"
            element={
              <PublicRoute>
                <UpdatePassword />
              </PublicRoute>
            }
          />
          <Route
            path="/privacy-policy"
            element={
              <PublicRoute>
                <PrivacyPolicy />
              </PublicRoute>
            }
          />
          <Route
            path="/terms-of-use"
            element={
              <PublicRoute>
                <TermsOfUse />
              </PublicRoute>
            }
          />
          <Route
            path="/"
            element={
              <PublicRoute>
                <RootRoute />
              </PublicRoute>
            }
          />
          <Route
            path="/manage"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <ManageLayout />
                </AdminRoute>
              </ProtectedRoute>
            }
          >
            <Route
              index
              element={<Navigate to="marketing-activities" replace />}
            />
            <Route path="marketing-activities" element={<Activities />} />
            <Route
              path="store-activities"
              element={<ManageStoreActivities />}
            />
            <Route path="import" element={<Import />} />
            <Route path="periods" element={<ManagePeriods />} />
            <Route path="products" element={<ManageProducts />} />
            <Route path="brands" element={<ManageBrands />} />
            <Route path="suppliers" element={<ManageSuppliers />} />
            <Route path="stores" element={<ManageStores />} />
            <Route path="banners" element={<ManageBanners />} />
            <Route path="store-sets" element={<ManageStoreSets />} />
            <Route path="agencies" element={<ManageAgencies />} />
            <Route path="territories" element={<ManageTerritories />} />
            <Route path="reps" element={<ManageReps />} />
            <Route path="users" element={<ManageUsers />} />
            <Route path="auditors" element={<ManageAuditors />} />
          </Route>
          <Route
            path="/about"
            element={
              <ProtectedRoute>
                <About />
              </ProtectedRoute>
            }
          />
          <Route
            path="/proofing"
            element={
              <ProtectedRoute>
                <RepOrAdminRoute>
                  <ProofingRouteSwitch />
                </RepOrAdminRoute>
              </ProtectedRoute>
            }
          >
            {/* Nested route: capture overlay renders via Outlet in Proofing (rep only) */}
            <Route path="store/:storeId/capture" element={<ProofCapture />} />
          </Route>
          <Route
            path="/approbation"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <Approbation />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/approbation/:id"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <ApprobationById />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/audit"
            element={
              <ProtectedRoute>
                <AuditorRoute>
                  <Audit />
                </AuditorRoute>
              </ProtectedRoute>
            }
          />
          {import.meta.env.DEV && (
            <Route path="/dev/reports/planning" element={<ReportPreview />} />
          )}
        </Routes>
      </BrowserRouter>
    </Theme>
  );
}

function AppWithTheme() {
  return (
    <ThemeProvider>
      <ThemedApp />
    </ThemeProvider>
  );
}

function ThemedApp() {
  const { theme } = useTheme();
  return (
    <AuthProvider>
      <UserProfileProvider>
        <App appearance={theme} />
        <CookieConsentBanner />
      </UserProfileProvider>
    </AuthProvider>
  );
}

export default AppWithTheme;
